/**
 * Payabli deposit payment for the public quote page — the Payabli analogue of
 * the Stripe `DepositCTA`.
 *
 * Stripe redirects to hosted Checkout; Payabli captures the card INLINE via the
 * embedded component, which returns a short-lived token we POST to the
 * estimate's `payabli-deposit` route. The server charges the estimate's own
 * required deposit — the customer never sends an amount, only a token.
 *
 * The card service fee is disclosed CONDITIONALLY (the embedded component
 * reveals the rail and card funding only to the server, after tokenization):
 * when the tenant surcharges, a note says the difference applies to credit
 * cards only, the card-rail total reads as a ceiling ("up to"), and the charge
 * sends `surchargeDisclosed: true` so the backend may apply the fee — but only
 * when the tokenized card reports CREDIT funding. The
 * disclosed figure comes from `getDepositSurchargePreview`, the same number
 * the claim stamps, so disclosed === charged for a card payer.
 */
import { useEffect, useState } from 'react';
import { CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import {
  PayabliPaymentMethodSetupForm,
  type PayabliCustomerData,
} from '@/components/organisms/payments/PayabliPaymentMethodSetupForm';
import { defaultPayabliRail, type PayabliRail } from '@/lib/payments/payabli';
import type { PublicPaymentConfig } from '@/types/public';
import {
  computePaymentMethodDiscount,
  formatDiscountPercent,
} from '@/lib/payments/paymentMethodDiscount';
import { cardServiceFeeNote } from '@/lib/payments/cardServiceFeeNote';
import { SecureNote } from '@/components/organisms/payments/PaymentRailSwitch';

export interface PayabliDepositPaymentProps {
  /** The estimate's required deposit — display only; the server re-derives it. */
  depositAmount: number;
  config: NonNullable<PublicPaymentConfig['payabli']>;
  /**
   * Payor identity. `contactId` is REQUIRED by paypoints that demand a customer
   * identifier — the server exchanges the temp token binding this same id, and
   * Payabli rejects a mismatched payor on charge.
   */
  contactId?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  /**
   * Charge the deposit; resolves with the settled outcome once the server has
   * committed the payment ('SUCCEEDED', or 'PENDING' for an ACH capture still
   * clearing). A terminal failure must REJECT, never resolve.
   */
  onCharge: (
    temporaryToken: string,
    /** True only when the payer is on the CARD rail and saw the fee note. */
    surchargeDisclosed: boolean,
  ) => Promise<'SUCCEEDED' | 'PENDING'>;
  /** Card service-fee disclosure (deposit surcharge preview), when the tenant surcharges. */
  surcharge?: {
    enabled: boolean;
    surchargePercent: number;
    achCheckDiscountEnabled?: boolean;
    achCheckDiscountPercent?: number;
    discountCents?: number;
    surchargeAmount?: number;
  };
  disabled?: boolean;
  /** Tailwind classes for the collapsed CTA, so both quote variants match. */
  buttonClassName?: string;
  /**
   * Start with the payment form open instead of the collapsed "Pay deposit"
   * button — the quote page's Approve and pay. Turning it on after mount opens
   * the form too; turning it off never closes one the payer is using.
   */
  defaultOpen?: boolean;
}

function fmt(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Split a display name into the optional first/last the paypoint may want.
 * Best-effort only: `customerNumber` is the field that actually matters, and a
 * one-word or empty name simply contributes nothing.
 */
function splitName(name?: string | null): { firstName?: string; lastName?: string } {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function PayabliDepositPayment({
  depositAmount,
  config,
  contactId,
  customerName,
  customerEmail,
  onCharge,
  surcharge,
  disabled,
  buttonClassName = 'h-12 w-full rounded-2xl bg-black text-white shadow-[0_10px_24px_rgba(38,52,61,0.18)] transition hover:bg-black/90',
  defaultOpen = false,
}: PayabliDepositPaymentProps) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  // The embedded form re-enables its own submit button the moment tokenization
  // succeeds — BEFORE the server-side charge below finishes. Track our own
  // in-flight state and swap the form for a processing view, so a second click
  // can't tokenize again and fire a second charge (a fresh token means a
  // different idempotency key, so the server would not dedupe it).
  const [charging, setCharging] = useState(false);
  // Why the last attempt was refused, kept until the next one. The toast closes
  // on its own and the embedded form comes back fresh after the processing
  // view, so without this a refusal ("a deposit payment is already being
  // processed") vanished and the page looked as if nothing had happened.
  const [chargeError, setChargeError] = useState<string | null>(null);
  // An ACH capture settles asynchronously (PENDING): the estimate stays
  // uncollected and this CTA stays mounted, so without a terminal state of its
  // own the page would spin on "Processing…" forever.
  const [pendingSettlement, setPendingSettlement] = useState(false);
  // The rail the embedded form is showing. Both are reachable now that the
  // form renders the Card | Bank switch, so the fee, the total on both buttons
  // and the disclosure attestation follow the payer's choice.
  const [rail, setRail] = useState<PayabliRail>(() =>
    defaultPayabliRail({ card: config.acceptCard, ach: config.acceptACH }),
  );

  // The fee rides the card rail only (#2763), and the total shown must be the
  // total charged for the rail in play.
  const directDiscount = surcharge?.achCheckDiscountEnabled === true;
  const bankQuote = computePaymentMethodDiscount(surcharge, Math.round(depositAmount * 100), 'ACH');
  const railDiscount = rail === 'ach' ? bankQuote.discountCents / 100 : 0;
  const railFee =
    !directDiscount && surcharge?.enabled === true && config.acceptCard && rail === 'card'
      ? (surcharge.surchargeAmount ?? 0)
      : 0;
  const displayTotal = depositAmount - railDiscount + railFee;
  const feeText =
    directDiscount && config.acceptACH && bankQuote.discountCents > 0
      ? `Bank transfer: ${fmt(bankQuote.chargeCents / 100)} — save ${fmt(bankQuote.discountCents / 100)} (${formatDiscountPercent(bankQuote.percent)}).${config.acceptCard ? ` Credit and debit cards: ${fmt(depositAmount)}.` : ''}`
      : !directDiscount && surcharge?.enabled === true
        ? cardServiceFeeNote({
            surchargePercent: surcharge.surchargePercent,
            feeAmount: surcharge.surchargeAmount ? fmt(surcharge.surchargeAmount) : null,
            achOrCheckPrice: depositAmount,
            cardPriceDifference: surcharge.surchargeAmount ?? null,
            cardOffered: config.acceptCard,
            achOffered: config.acceptACH,
            selectedRail: rail,
            // Same charge path as the invoice page (`settlePublicPayabliRowCharge`):
            // the claimed fee is added only when the tokenized card is CREDIT.
            creditCardOnly: true,
          })
        : '';
  const feeNote = feeText ? (
    <p className="text-center text-xs text-muted-foreground">{feeText}</p>
  ) : null;
  // Funding is resolved from the temp token AFTER this button is clicked, and a
  // debit, prepaid or BIN-less card is charged the bare deposit. On the card
  // rail the fee-inclusive figure is therefore a ceiling, not a price.
  const totalLabel = railFee > 0 ? `up to ${fmt(displayTotal)}` : fmt(displayTotal);

  if (!config.publicToken || !config.entryPoint || !contactId) {
    // No payor id means the paypoint rejects tokenization client-side, so a
    // button here could only ever fail. Same treatment as missing credentials.
    return (
      <p className="text-center text-sm text-muted-foreground">
        Online deposit payment is temporarily unavailable. Please contact the business to pay your
        deposit.
      </p>
    );
  }

  if (pendingSettlement) {
    // Transient only: the awaited refetch in onCharge hands the settling
    // state to the quote page (server-derived), which replaces this slot.
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
        <p className="font-semibold">Payment submitted</p>
        <p className="mt-0.5 text-sm opacity-90">
          {rail === 'card'
            ? 'Your payment is being confirmed. The deposit will update automatically — no further action is needed.'
            : 'Your bank transfer is processing. The deposit will be confirmed automatically once it clears — no further action is needed.'}
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="flex w-full flex-col gap-3">
        <Button className={buttonClassName} onClick={() => setOpen(true)} disabled={disabled}>
          <CreditCard className="mr-2 h-4 w-4" />
          {`Pay deposit ${totalLabel}`}
        </Button>
        {feeNote}
        <SecureNote>Secure, encrypted connection</SecureNote>
      </div>
    );
  }

  if (charging) {
    return (
      <div className="flex flex-col items-center gap-2 py-6">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        <p className="text-sm text-muted-foreground">Processing your payment…</p>
      </div>
    );
  }

  const customerData: PayabliCustomerData = {
    customerNumber: contactId,
    ...splitName(customerName),
    billingEmail: customerEmail || undefined,
  };

  return (
    <div className="space-y-3">
      {chargeError ? (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          {chargeError}
        </p>
      ) : null}
      <PayabliPaymentMethodSetupForm
        appearance="customer"
        holderName={customerName ?? undefined}
        publicToken={config.publicToken}
        entryPoint={config.entryPoint}
        mode={config.mode}
        submitLabel={`Pay ${totalLabel}`}
        // Only the rails the tenant accepts — the server-side charge rejects
        // the rest, so offering one would only fail the payer after data entry.
        // The backend always sends both flags; no client-side `?? true`.
        methods={{ card: config.acceptCard, ach: config.acceptACH }}
        onRailChange={setRail}
        errorTitle="Payment failed"
        customerData={customerData}
        onComplete={async (result) => {
          setCharging(true);
          setChargeError(null);
          try {
            // Attest the fee note only for the rail that was actually shown a
            // fee — the backend adds the service fee only when the page says
            // it disclosed one.
            const outcome = await onCharge(
              result.temporaryToken,
              !directDiscount &&
                surcharge?.enabled === true &&
                config.acceptCard &&
                rail === 'card',
            );
            if (outcome === 'PENDING') {
              // The estimate isn't collected yet, so nothing will unmount this
              // component — swap to a terminal "submitted" state ourselves.
              setPendingSettlement(true);
              setCharging(false);
            }
            // SUCCEEDED: the parent's refetch flips the page to its paid
            // state and unmounts this form; keep the processing view up.
          } catch (error) {
            // Back to the form so the customer can retry with a fresh entry;
            // rethrow so the embedded form renders its own error alert too.
            setCharging(false);
            const message =
              error instanceof Error && error.message
                ? error.message
                : 'Deposit payment could not be completed. Please try again.';
            setChargeError(message);
            toast.error(message);
            throw error;
          }
        }}
      />
      {feeNote}
      <Button variant="ghost" className="w-full" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}
